import { Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useBranding } from "@/hooks/useBranding";
import { useAuthState } from "@/hooks/useAuthState";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import BrandLogo from "@/components/BrandLogo";
import { Menu, X, ChevronDown, CreditCard, Banknote, Download, Upload, Clock, Landmark, ArrowUpFromLine, BarChart3, ArrowLeftRight, Users, TrendingUp, Coins, Copy, Target, TestTube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeSwitch } from "@/components/ThemeSwitch";
import LandingDesktopNav from "./LandingDesktopNav";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { filterMenusForAuth } from "./navConfig";

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 72;
  window.scrollTo({ top, behavior: "smooth" });
};

export { scrollToSection };

interface MobileMenuItem {
  icon: typeof CreditCard;
  title: string;
  href: string;
  moduleKey?: string;
}

interface MobileSection {
  label: string;
  moduleKey: string;
  items: MobileMenuItem[];
}

const allMobileMenuSections: MobileSection[] = [
  {
    label: "Crypto",
    moduleKey: "landing_crypto",
    items: [
      { icon: CreditCard, title: "Buy", href: "/app/trade/buy" },
      { icon: Banknote, title: "Sell", href: "/app/trade/sell" },
      { icon: Download, title: "Deposit", href: "/app/crypto/deposit" },
      { icon: Upload, title: "Withdraw", href: "/app/crypto/withdraw" },
      { icon: Clock, title: "History", href: "/app/transactions" },
    ],
  },
  {
    label: "Fiat",
    moduleKey: "landing_fiat",
    items: [
      { icon: Landmark, title: "Deposit", href: "/app/fiat/deposit" },
      { icon: ArrowUpFromLine, title: "Withdraw", href: "/app/fiat/withdraw" },
      { icon: Clock, title: "History", href: "/app/transactions" },
    ],
  },
  {
    label: "Trade",
    moduleKey: "landing_trade",
    items: [
      { icon: BarChart3, title: "Spot", href: "/app/spot", moduleKey: "spot_trading" },
      { icon: ArrowLeftRight, title: "Convert", href: "/app/exchange", moduleKey: "wallets" },
      { icon: Users, title: "P2P", href: "/app/p2p", moduleKey: "p2p" },
      { icon: TrendingUp, title: "Futures USD-M", href: "/app/futures", moduleKey: "futures" },
      { icon: Coins, title: "Futures COIN-M", href: "/app/futures?type=coinm", moduleKey: "futures" },
      { icon: Copy, title: "Copy Trading", href: "/app/copy-trading", moduleKey: "copy_trading" },
      { icon: Target, title: "Binary Trading", href: "/app/binary", moduleKey: "binary_trading" },
      { icon: TestTube, title: "Demo Trading", href: "/app/demo-wallet", moduleKey: "demo_trading" },
    ],
  },
];

const allMobileStandaloneLinks = [
  { label: "Notes", href: "/notes", moduleKey: "landing_notes" },
  { label: "Markets", href: "/app/spot", moduleKey: "landing_markets" },
];

export default function StickyNav() {
  const { appName } = useBranding();
  const { user, isAuthed } = useAuthState();

  const { isModuleEnabled, isLoading: modulesLoading } = useModuleSettings();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Filter mobile sections by module enabled state, then by auth (guest allowlist).
  const mobileMenuSections = useMemo(() => {
    const moduleFiltered = allMobileMenuSections
      .filter((s) => isModuleEnabled(s.moduleKey))
      .map((s) => ({
        ...s,
        items: s.items.filter((item) => !item.moduleKey || isModuleEnabled(item.moduleKey)),
      }))
      .filter((s) => s.items.length > 0);
    return filterMenusForAuth(moduleFiltered, isAuthed);
  }, [isModuleEnabled, isAuthed]);

  const mobileStandaloneLinks = useMemo(() => {
    return allMobileStandaloneLinks.filter((l) => isModuleEnabled(l.moduleKey));
  }, [isModuleEnabled]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass-strong border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo className="h-9 max-w-[200px] object-contain" />
        </Link>

        {/* Desktop nav — dropdowns */}
        <div className="hidden lg:flex items-center">
          <LandingDesktopNav />
        </div>

        <div className="hidden lg:flex items-center gap-2 xl:gap-3">
          <ThemeSwitch size="sm" />
          {isAuthed ? (
            <Button asChild size="sm" variant="glow" className="rounded-full font-semibold">
              <Link to="/app/dashboard">Go to Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">Sign In</Link>
              </Button>
              <Button asChild size="sm" variant="glow" className="rounded-full font-semibold">
                <Link to="/signup">Get Started</Link>
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <ThemeSwitch size="sm" />
          <button
            className="p-2 text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden glass-strong border-b border-border/50 px-6 py-4 space-y-2">
          {modulesLoading ? (
            <div className="space-y-2" aria-hidden>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-8 w-full rounded bg-muted/40 animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {mobileMenuSections.map((section) => (
                <Collapsible key={section.label}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-semibold text-foreground">
                    {section.label}
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-2 space-y-1 pb-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.href + item.title}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2.5 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Icon className="h-4 w-4 text-primary" />
                          {item.title}
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              ))}
              {mobileStandaloneLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block py-2 text-sm font-semibold text-foreground"
                >
                  {link.label}
                </Link>
              ))}
            </>
          )}
          <div className="flex gap-3 pt-2">
            {isAuthed ? (
              <Button asChild size="sm" variant="glow" className="flex-1 rounded-full">
                <Link to="/app/dashboard" onClick={() => setMobileOpen(false)}>Go to Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="flex-1">
                  <Link to="/login" onClick={() => setMobileOpen(false)}>Sign In</Link>
                </Button>
                <Button asChild size="sm" variant="glow" className="flex-1 rounded-full">
                  <Link to="/signup" onClick={() => setMobileOpen(false)}>Get Started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
